// ---------------------------------------------------------------------------
// 报销单自适应页型打印：尽量都用 A5 横向。报销单块（表单+发票凭证图）内部自然
// 流式分页，内容跨多张 A5 续排；仅当"单张发票图"自身高度超过 A5 可打印高度
// （不可拆分，如 A4 竖版整页发票）时，该图才改用 A4 纵向整页，其余仍为 A5，
// 单次打印任务混合页型输出。
//
// 为什么替代 print-js：
//  1) print-js 的 targetStyles:['*'] 会把克隆瞬间每个元素的计算样式（含宽高）冻结成
//     内联样式，难以与 @page 页型 / 分页控制配合（此前发票图片重叠 bug 的隐患）；
//  2) 需要按内容高度给不同元素指定不同纸张（CSS 命名页 page + @page）。
//
// 宽度零缩放的原理：内容统一按自然宽度排版后等比 zoom 到纸面可用宽度；
// A5 横向（210×148）与 A4 纵向（210×297）同宽 210mm，两种页型缩放比例一致，
// 任何页型上文字大小一致，宽度不会被压小。
//
// 兼容：命名页需 Chrome/Edge 85+ / Firefox 110+；不支持时整单退回 A4 纵向（等价旧效果）。
// ---------------------------------------------------------------------------
const PAGE_MARGIN_MM = 6; // @page 四边边距
const A5_W_MM = 210, A5_H_MM = 148; // A5 横向
const A4_W_MM = 210, A4_H_MM = 297; // A4 纵向
const MM_TO_PX = 96 / 25.4; // CSS 参考分辨率 96dpi 下 1mm 对应像素

export function printReiAdaptive(rootEl, opts) {
  opts = opts || {};
  const ignoreIds = opts.ignoreIds || [];
  // 内容设计宽度：默认取 A5 可打印宽度（198mm≈748px）。报销单表单为百分比流式表格，
  // 按 748px 排版后缩放比≈1，文字以原始字号打印（较 860px 设计宽放大约 15%）；
  // 内容含固定宽度元素超宽时 scrollWidth 自动撑大 w0，缩放比回落，不会裁切
  const baseWidth = opts.baseWidth || 748;
  const onAfterPrint = opts.onAfterPrint || function () {};
  const onReady = opts.onReady || function () {}; //打印弹窗即将弹出时回调（用于关闭"正在准备打印"遮罩）
  const onError = opts.onError || function () {};
  if (!rootEl) {
    try { onError(); } catch (e) { /* 忽略 */ }
    return;
  }

  // 纸面可用区域（CSS px）
  const usableWPx = (A5_W_MM - PAGE_MARGIN_MM * 2) * MM_TO_PX; // 198mm ≈ 748px
  const a5UsableHPx = (A5_H_MM - PAGE_MARGIN_MM * 2) * MM_TO_PX; // 136mm ≈ 514px
  // 命名页支持检测（一次任务混合 A5/A4 页型）；不支持则整单退回 A4 纵向
  const namedPages = typeof CSS !== 'undefined' && !!CSS.supports && CSS.supports('page', 'a4sec');

  // ---- 1. 建隐藏 iframe（离屏但保留真实视口宽，避免拷贝进来的响应式媒体查询误命中） ----
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:1600px;height:1000px;border:0;';
  document.body.appendChild(iframe);

  let cleaned = false;
  function cleanup() {
    if (cleaned) return;
    cleaned = true;
    try { onAfterPrint(); } catch (e) { /* 清理失败不影响打印 */ }
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }

  const doc = iframe.contentWindow.document;
  const clone = rootEl.cloneNode(true);
  ignoreIds.forEach(function (id) {
    const el = clone.querySelector('[id="' + id + '"]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  });

  // ---- 2. 每个报销单块包一层 section，独立成页 ----
  const wrap = doc.createElement('div');
  wrap.className = 'printRoot';
  const sections = [];
  Array.prototype.slice.call(clone.children).forEach(function (child) {
    const sec = doc.createElement('section');
    sec.className = 'printSection';
    sec.appendChild(child);
    wrap.appendChild(sec);
    sections.push(sec);
  });

  // 汇总页面样式：生产环境为同源外链 CSS（可读规则），开发环境为 style 标签；
  // 跨域样式表读不到规则时退回 @import 引入原链接
  function collectCss() {
    const imports = [];
    let rules = '';
    for (let i = 0; i < document.styleSheets.length; i++) {
      const sheet = document.styleSheets[i];
      try {
        const rs = sheet.cssRules;
        if (!rs) continue;
        for (let r = 0; r < rs.length; r++) rules += rs[r].cssText + '\n';
      } catch (e) {
        if (sheet.href) imports.push('@import url("' + sheet.href + '");');
      }
    }
    return imports.join('\n') + '\n' + rules;
  }

  const styleEl = doc.createElement('style');
  styleEl.textContent = collectCss() +
    // 打印基础样式（不再强制 .page 固定高度，让内容自然流排以测量真实高度）
    'html,body{margin:0;padding:0;background:#fff}' +
    'body{width:' + baseWidth + 'px;-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
    'table{border-collapse:separate}' +
    '.printRoot{margin:0}' +
    'tr{break-inside:avoid;page-break-inside:avoid}' +
    //发票容器必须用 padding 而非 margin 撑开与表单的间距：margin 不计入 getBoundingClientRect，
    //逐页装填/垂直居中按 rect 算高度会漏掉这 20px，贴页时溢出产生空白页
    '.printInvoiceImgs{padding-top:20px}' +
    //img 必须 block：默认 inline 的图片底部有 baseline descent 间隙（约4px/张），
    //计入实际渲染高度但不在装填计算里，多张图累计溢出页高会推挤产生空白页
    '.printInvoiceImgs img{display:block;max-width:100%;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid}';
  doc.head.appendChild(styleEl);
  doc.body.appendChild(wrap);

  // ---- 3. 等图片加载完成再量高（克隆的 img 会重新加载，同地址命中缓存） ----
  function waitImages() {
    //必须等每张图真正加载成功（naturalWidth>0）再量高：克隆 img 的首次请求偶发失败
    //（并发去重/服务瞬时错误/弱网），error 即放行会按坏图尺寸（约16px）做装填与缩放
    //决策，图片随后加载完成后布局被撑爆，表单和发票凭证之间出现空白页。
    //失败自动重置 src 重载一次；纯事件驱动（轮询定时器在打印虚拟时间下会烧穿预算）
    return new Promise(function (resolve) {
      const imgs = doc.images;
      let need = 0;
      let settled = 0;
      let done = false;
      const ok = function () { if (!done && ++settled >= need) { done = true; resolve(); } };
      const retry = function () {
        if (!this.__rpRetry) { this.__rpRetry = 1; this.src = this.src; return; } //瞬时失败：重载一次
        ok(); //重载仍失败：照常打印，不永久卡死
      };
      Array.prototype.forEach.call(imgs, function (img) {
        if (img.naturalWidth) return; //已成功加载
        need++;
        img.addEventListener('load', ok);
        img.addEventListener('error', retry);
        if (img.complete && !img.naturalWidth) retry.call(img); //已处于失败态：立即重载
      });
      if (!need) resolve();
      setTimeout(function () { done = true; resolve(); }, 30000); //兜底：请求挂起也不过久阻塞打印
    });
  }

  waitImages().then(function () {
    // ---- 4. 定自然宽度 → 计算缩放 → 逐节量高分级（A5 / A4） ----
    let w0 = Math.max(baseWidth, wrap.scrollWidth || 0);
    doc.body.style.width = w0 + 'px';
    w0 = Math.max(baseWidth, wrap.scrollWidth || w0);
    const zoomR = usableWPx / w0; // ≤1：把内容等比压进可用宽度

    // 尽量都用 A5：报销单块内部自然流式分页（表单、发票图跨页续排），
    // 仅当"单张不可拆分的发票图"自身高度超过 A5 可打印高度时，该图才改用 A4 纵向整页
    const a4UsableHPx = (A4_H_MM - PAGE_MARGIN_MM * 2) * MM_TO_PX; // A4 纵向可用高 ≈ 285mm
    const cap = a5UsableHPx - 20; // A5 单页可用高度（视觉 px，留 20px≈5mm 安全余量：渲染取整/测量误差溢出会产生"空白页"）
    const supportsZoom = 'zoom' in wrap.style;
    sections.forEach(function (sec) {
      // 测量表单等非发票图内容的视觉高度（节 zoom 后 rect 已含缩放，需重测）
      function measure() {
        const natH = sec.getBoundingClientRect().height; // 含全部发票图的整节高度
        let allM = 0; // 全部发票图占位高度（含 10px 下边距）
        const imgs = sec.querySelectorAll('.printInvoiceImgs img');
        Array.prototype.forEach.call(imgs, function (img) {
          allM += img.getBoundingClientRect().height + 10;
        });
        //节设置 padding-top 后，首子元素的 margin-top 不再折叠出节外，必须计入高度，否则溢出会产生空白页
        const fc = sec.firstElementChild;
        const extraTop = fc ? (parseFloat(doc.defaultView.getComputedStyle(fc).marginTop) || 0) : 0;
        return { imgs: imgs, baseV: (natH - allM + extraTop) * zoomR }; // baseV：表单等非发票图内容的视觉高度
      }
      let z = 1; // 节级缩放系数（1 = 不缩放）
      let m = measure();
      if (m.baseV > cap && supportsZoom) {
        // 表单本身超过一页 A5：若不处理，表单尾部（审批/签章区）会独占一页、发票图放不下
        // 被推走，报销单与发票凭证之间夹出近空白页。整节微缩（下限 0.8，避免字太小）
        // 让报销单回到一页内，再走正常装填。
        // 目标留 8px 余量：精确缩到 cap 时，重测值与 cap 只差浮点/取整级 epsilon，
        // 下方 m.baseV > cap 的判断会被误触发而整体放弃装填（凭证图落入自然分页，
        // Chrome 对超过页高的不可拆图会先推出一张全空白页再放图），签章区也会因
        // 1-2px 舍入溢出成近空白页
        z = Math.max(0.8, (cap - 8) / m.baseV);
        sec.style.zoom = z;
        m = measure(); // zoom 后重测
        // 缩到下限仍超页（极端长表单）不再放弃处理：baseV > cap 时首页剩余容量为 0，
        // 表单自然跨页、凭证图各自成页——若这里 return 跳过装填，未处理的 DOM 自然
        // 分页会产生空白页
      }
      const baseV = m.baseV;
      const imgs = m.imgs;

      // 第一步：超高的发票图包 A4 纵向整页（page 属性只对块级元素生效，包一层再指定）
      Array.prototype.forEach.call(imgs, function (img) {
        const h = img.getBoundingClientRect().height;
        //判定阈值用 A5 整页可用高（a5UsableHPx≈514px）留 14px 余量，而非保守装填上限
        //cap（≈494px）：超 cap 的图会被装填逻辑推到独享的新页，页内只有它自己（+10px
        //下边距），只要 ≤ 整页高就能放下。用 cap 判定会把横版 PDF 发票（图高约 499px、
        //刚好放得下）也推去 A4，导致所有凭证页型都变大
        if (h * zoomR > a5UsableHPx - 14) {
          const holder = doc.createElement('div');
          holder.className = 'a4sec';
          img.parentNode.insertBefore(holder, img);
          holder.appendChild(img);
          // A4 整页发票图同样垂直居中（视觉 px 换算回未缩放 px：节 zoom z 与全局 zoomR 叠加，除以 zoomR*z）
          const padA4 = Math.max(0, (a4UsableHPx - 20 - h * zoomR) / 2) / (zoomR * z);
          holder.style.paddingTop = padA4.toFixed(1) + 'px';
          //图高超过一整张 A4（超长小票照）：max-height 缩到一页内，否则溢出的底部
          //会 spill 到下一页，形成一张只有图尾一条边的近空白页
          if (h * zoomR > a4UsableHPx - 20) {
            img.style.maxHeight = ((a4UsableHPx - 20) / (zoomR * z)).toFixed(1) + 'px';
            img.style.width = 'auto';
          }
        }
      });

      // 第二步：A5 发票图逐页装填（装不下就换新页），每一页有剩余空间就垂直居中。
      // margin 在分页起点会被浏览器截断，居中必须用 padding
      const divs = sec.querySelectorAll('.printInvoiceImgs');
      const newPages = [];
      let curDiv = null; // 当前 A5 页的发票图容器（原始 div 或新分页 div）
      let pageV = 0; // 当前 A5 页已装内容（视觉 px）
      let page0V = 0; // 第 1 页（表单页）里的发票图高度
      let firstPage = true; // 第 1 页容量需扣除表单高度
      let afterA4 = false; // 刚经过 A4 整页图，后续发票图需另起新 A5 页
      Array.prototype.forEach.call(divs, function (div) {
        Array.prototype.slice.call(div.children).forEach(function (child) {
          if (child.nodeType === 1 && (' ' + child.className + ' ').indexOf(' a4sec ') >= 0) {
            afterA4 = true; // A4 整页强制换页
            return;
          }
          if (child.tagName !== 'IMG') return;
          const v = (child.getBoundingClientRect().height + 10) * zoomR;
          const capNow = firstPage ? cap - baseV : cap;
          if (afterA4 || pageV + v > capNow) {
            //上一页到此结束：原始 div（第 1 页）也必须显式 break-after。
            //若靠自然分页，新页 div 的盒子会从第 1 页剩余空间开始渲染，其大块
            //居中 padding-top 落在第 1 页底部导致溢出，发票图被整体推走，级联产生空白页
            (curDiv || div).classList.add('pageBreakEnd');
            const nd = doc.createElement('div');
            nd.className = 'printInvoiceImgs';
            (curDiv || div).parentNode.insertBefore(nd, curDiv ? curDiv.nextSibling : div.nextSibling);
            newPages.push(nd);
            curDiv = nd;
            pageV = 0;
            firstPage = false;
            afterA4 = false;
          }
          if (!curDiv) curDiv = div; // 第 1 页直接用原始 div
          curDiv.appendChild(child);
          pageV += v;
          if (firstPage) page0V += v;
        });
      });
      // 第 1 页（表单+首批发票图）垂直居中（padding 会被节 zoom z 缩放，除以 zoomR*z）
      sec.style.paddingTop = (Math.max(0, cap - baseV - page0V) / 2 / (zoomR * z)).toFixed(1) + 'px';
      // 后续各发票图页垂直居中
      Array.prototype.forEach.call(newPages, function (nd) {
        let v = 0;
        Array.prototype.forEach.call(nd.querySelectorAll('img'), function (im) {
          v += (im.getBoundingClientRect().height + 10) * zoomR;
        });
        nd.style.paddingTop = (Math.max(0, cap - v) / 2 / (zoomR * z)).toFixed(1) + 'px';
      });
    });

    if ('zoom' in wrap.style) {
      wrap.style.zoom = zoomR;
    } else {
      // zoom 不可用（老 Firefox）：transform:scale + 负 margin 补偿布局高度
      sections.forEach(function (sec) {
        const natH = sec.getBoundingClientRect().height;
        sec.style.transformOrigin = '0 0';
        sec.style.transform = 'scale(' + zoomR + ')';
        sec.style.marginBottom = '-' + (natH * (1 - zoomR)) + 'px';
      });
    }

    // ---- 5. 注入 @page 页型规则并打印 ----
    const sectionCss = '.printSection{break-after:page;page-break-after:always}' +
      '.printSection:last-child{break-after:auto;page-break-after:auto}' +
      '.pageBreakEnd{break-after:page;page-break-after:always}';
    const pageCss = (namedPages
      ? '@page{size:' + A5_W_MM + 'mm ' + A5_H_MM + 'mm;margin:' + PAGE_MARGIN_MM + 'mm}' +
        '@page a4sec{size:' + A4_W_MM + 'mm ' + A4_H_MM + 'mm;margin:' + PAGE_MARGIN_MM + 'mm}' +
        '.a4sec{page:a4sec}'
      : '@page{size:' + A4_W_MM + 'mm ' + A4_H_MM + 'mm;margin:' + PAGE_MARGIN_MM + 'mm}') + sectionCss;
    const pageStyle = doc.createElement('style');
    pageStyle.textContent = pageCss;
    doc.head.appendChild(pageStyle);

    const win = iframe.contentWindow;
    const after = function () {
      win.removeEventListener('afterprint', after);
      cleanup();
    };
    win.addEventListener('afterprint', after);
    setTimeout(cleanup, 120000); // 兜底：个别环境 afterprint 不触发时移除 iframe
    win.focus();
    try { onReady(); } catch (e) { /* 回调异常不阻断打印 */ }
    win.print();
  }).catch(function () {
    try { onError(); } catch (e) { /* 忽略 */ }
    cleanup();
  });
}

export default printReiAdaptive;
